/**
 * Onboarding flow keys - Frontend Constants
 *
 * ⚠️ MUST MATCH BACKEND EXACTLY ⚠️
 * Backend source: @gfactor/server/src/enums/OnboardingFlowKey.ts
 *
 * When adding new flows:
 * 1. Add to backend enum first
 * 2. Copy the EXACT string value here
 * 3. Values are case-sensitive!
 */
export const OnboardingFlowKey = {
  TENANT_INITIAL: 'TENANT_INITIAL',
  // Add future flows here (copy from backend):
  // TENANT_ADVANCED_SETUP: 'TENANT_ADVANCED_SETUP',
  // TENANT_INTEGRATIONS: 'TENANT_INTEGRATIONS',
} as const;

export type OnboardingFlowKey =
  (typeof OnboardingFlowKey)[keyof typeof OnboardingFlowKey];

// =============================================================================
// FLOW CONFIGURATION
// =============================================================================

/**
 * Step definition for onboarding flows
 */
export interface OnboardingStep {
  key: string;
  order: number;
  title: string;
}

/**
 * Step keys for TENANT_INITIAL flow
 *
 * The conversational onboarding flow consists of 5 steps:
 * 1. Property creation (address/site)
 * 2. Spaces setup
 * 3. Assets creation
 * 4. Jobs/recurring jobs setup
 * 5. Completion (explicit finish step to prevent auto-completion)
 *
 * NOTE: Step 5 (COMPLETE) exists to prevent the backend from auto-completing
 * the flow when advancing to step 4. The backend auto-completes when
 * currentStep >= totalSteps, so we need an extra step that's only reached
 * when the user explicitly finishes.
 */
export const TenantInitialStepKey = {
  /** Step 1: Property creation - creates first site */
  PROPERTY: 'PROPERTY',
  /** Step 2: Spaces setup - creates spaces within the property */
  SPACES: 'SPACES',
  /** Step 3: Assets creation - creates assets and deploys them */
  ASSETS: 'ASSETS',
  /** Step 4: Jobs setup - creates recurring jobs/automations */
  JOBS: 'JOBS',
  /** Step 5: Completion - explicit finish step */
  COMPLETE: 'COMPLETE',
} as const;

export type TenantInitialStepKey =
  (typeof TenantInitialStepKey)[keyof typeof TenantInitialStepKey];

/**
 * Flow Configuration
 *
 * There are two main patterns:
 *
 * 1. TENANT-LEVEL FLOW (one user completes for the whole team)
 *    - completionMode: 'single_user'
 *    - stepTrackingMode: 'flow_level'
 *    - Only the user who STARTS the flow can ADVANCE/COMPLETE it
 *    - Backend enforces this via `started_by_user_id` check
 *
 * 2. PER-USER FLOW (each team member completes their own)
 *    - completionMode: 'all_roles'
 *    - stepTrackingMode: 'per_user'
 *    - requiredRoles: specify which roles must complete
 *    - Each user has their own progress record
 */
export interface OnboardingFlowConfig {
  /**
   * 'single_user' = One person completes for the entire tenant (e.g., initial setup)
   * 'all_roles'   = Each user with the specified roles must complete individually
   */
  completionMode: 'single_user' | 'all_roles';

  /**
   * Only used when completionMode is 'all_roles'.
   * Specifies which roles must complete this flow.
   * Empty array = no role restrictions (all users).
   */
  requiredRoles: ('admin' | 'operator' | 'fsp')[];

  /**
   * When true, new users joining the tenant will automatically need to complete this flow.
   * Only relevant for 'all_roles' flows.
   */
  applyToNewUsers: boolean;

  /**
   * 'flow_level' = Progress stored in tenant_onboarding_flow (one record per tenant)
   * 'per_user'   = Progress stored in user_onboarding_flow (one record per user)
   */
  stepTrackingMode: 'flow_level' | 'per_user';

  /**
   * Steps in this flow. For single-step flows, use one step.
   */
  steps: OnboardingStep[];
}

/**
 * Flow configurations - defines behavior for each onboarding flow
 *
 * TENANT_INITIAL: Initial tenant setup flow
 * - Multi-step flow with 5 steps: Property → Spaces → Assets → Jobs → Complete
 * - Completed at tenant level by the user who signed up
 * - Only the starting user can advance/complete the flow
 * - The ConversationalOnboarding component handles all steps internally
 * - Step 5 (COMPLETE) prevents auto-completion when advancing to Jobs step
 */
export const ONBOARDING_FLOWS: Record<OnboardingFlowKey, OnboardingFlowConfig> =
  {
    [OnboardingFlowKey.TENANT_INITIAL]: {
      completionMode: 'single_user',
      requiredRoles: [], // Ignored for single_user mode
      applyToNewUsers: false,
      stepTrackingMode: 'flow_level',
      steps: [
        {
          key: TenantInitialStepKey.PROPERTY,
          order: 1,
          title: 'Add Property',
        },
        {
          key: TenantInitialStepKey.SPACES,
          order: 2,
          title: 'Add Spaces',
        },
        {
          key: TenantInitialStepKey.ASSETS,
          order: 3,
          title: 'Add Assets',
        },
        {
          key: TenantInitialStepKey.JOBS,
          order: 4,
          title: 'Create Jobs',
        },
        {
          key: TenantInitialStepKey.COMPLETE,
          order: 5,
          title: 'Complete',
        },
      ],
    },
  };

// =============================================================================
// HELPER FUNCTIONS
// =============================================================================

/**
 * Get flow configuration by key
 */
export function getFlowConfig(
  flowKey: OnboardingFlowKey
): OnboardingFlowConfig {
  return ONBOARDING_FLOWS[flowKey];
}

/**
 * Check if a flow uses tenant-level tracking (single_user mode)
 */
export function isTenantLevelFlow(flowKey: OnboardingFlowKey): boolean {
  const config = ONBOARDING_FLOWS[flowKey];
  return config.completionMode === 'single_user';
}

/**
 * Get total number of steps in a flow
 */
export function getTotalSteps(flowKey: OnboardingFlowKey): number {
  return ONBOARDING_FLOWS[flowKey].steps.length;
}

/**
 * Get step by order number (1-based)
 */
export function getStepByOrder(
  flowKey: OnboardingFlowKey,
  order: number
): OnboardingStep | undefined {
  return ONBOARDING_FLOWS[flowKey].steps.find((s) => s.order === order);
}

/**
 * Get step by key
 */
export function getStepByKey(
  flowKey: OnboardingFlowKey,
  stepKey: string
): OnboardingStep | undefined {
  return ONBOARDING_FLOWS[flowKey].steps.find((s) => s.key === stepKey);
}

/**
 * Check if given step order is the last step
 */
export function isLastStep(flowKey: OnboardingFlowKey, order: number): boolean {
  return order === getTotalSteps(flowKey);
}

// Test flag to force onboarding display (for development/testing)
export const FORCE_ONBOARDING_FOR_TESTING = false;
